/**
 * /fr/roadmap-produit — product roadmap concept page (French).
 * Links /fr/recherche-produit + /fr/prd-document-exigences-produit.
 */
import { MarketingShell } from './MarketingShell';

export function FrRoadmapProduit() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/roadmap-produit"
      hreflangLocales={['fr']}
      documentTitle="Roadmap produit : modèle, exemple et bonnes pratiques · Draftlize"
      documentDescription="Construire une roadmap produit claire : formats, exemple, et comment la relier aux décisions pour qu'elle reste vraie quand le périmètre bouge."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Roadmap</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.4vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            La roadmap produit qui survit<br />
            <em>au changement de périmètre.</em>
          </h1>
          <p className="lede">
            Une roadmap produit montre <em>où</em> va le produit et <em>pourquoi</em> : pas chaque détail, mais la direction et l'ordre. Le difficile n'est en général pas de la créer, mais qu'elle finit en « diapositive figée » : trois semaines plus tard une décision change, la roadmap non, et plus personne ne s'y fie. Voici les formats, un exemple, et comment la relier aux décisions pour qu'elle reste vraie.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Des formats de roadmap qui marchent.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong> — évite la fausse précision des dates ; idéal au début.</li>
            <li><strong>Par thèmes / résultats</strong> — ordonnez par objectifs, pas par fonctionnalités.</li>
            <li><strong>Temporel (trimestres)</strong> — quand les dépendances imposent des dates dures.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>Mini-exemple (Now/Next/Later) :</strong> Now — réduire la friction d'onboarding · Next — facturation en self-service · Later — espaces d'équipe. Chaque entrée porte un <em>pourquoi</em> : la décision qui l'a mise sur la roadmap. C'est exactement ce que la version en diapos perd.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Figée vs vivante</span>
            <h2 className="serif">Roadmap-diapo <em>vs roadmap qui vit avec les décisions.</em></h2>
          </div>
          <p>Une roadmap n'est que la pointe visible d'une chaîne de décisions. Séparez-les, et elle vieillit en silence.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Roadmap reliée (Draftlize)</th>
              <th>Roadmap-diapo</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Le « pourquoi » de chaque entrée</td><td className="ours yes">relié à une carte de décision</td><td className="no">dans les têtes / éparpillé</td></tr>
            <tr><td>Quand une décision change</td><td className="ours yes">l'entrée passe en « stale »</td><td className="no">la roadmap reste fausse</td></tr>
            <tr><td>Trois semaines plus tard</td><td className="ours yes">à jour ou signalée</td><td className="no">plus personne ne s'y fie</td></tr>
            <tr><td>Un agent IA l'utilise</td><td className="ours yes">lit le contexte via MCP</td><td className="partial">diapo plate</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La plupart des roadmaps n'étaient pas mal planifiées : personne ne les a simplement mises à jour dans les trois semaines depuis la dernière décision.
        </blockquote>
        <cite>Reliez la roadmap aux décisions et elle se met à jour d'elle-même quand la décision change. C'est Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Roadmap ou backlog ?', a: 'La roadmap montre la direction et l\'ordre au niveau des résultats ; le backlog est la liste priorisée du travail concret. La roadmap répond « pourquoi / vers où », le backlog « quoi ensuite ».' },
            { q: 'Quel horizon temporel ?', a: 'Aussi loin que vous pouvez donner une direction honnête ; souvent Now/Next/Later plutôt que des trimestres fixes. La fausse précision des dates est la première raison qu\'une roadmap perde en crédibilité.' },
            { q: 'Comment gérer les changements de périmètre ?', a: 'Si la décision derrière une entrée change, l\'entrée doit changer. Dans Draftlize, les deux sont reliées : la décision modifiée passe la partie concernée de la roadmap en « stale ».' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          À voir aussi : <a href="/fr/recherche-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>recherche produit</a> · <a href="/fr/prd-document-exigences-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a>
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Une roadmap qui se met à jour d'elle-même <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Construisez votre roadmap en cartes reliées, pas en diapo. Si une décision change, la roadmap se met à jour d'elle-même — pas trois semaines après.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
